<script setup lang="ts">
import type { Item as DailyReportCardListItem } from '@/components/descLists/DailyReportCardJobsDescList.vue';
import type { AmPmJobs } from '@/models/reports';

import { computed } from 'vue';

import { percentFormat } from '@/utils/format';
import DailyReportCardJobsDescList from '@/components/descLists/DailyReportCardJobsDescList.vue';
import SimpleLoader from '@/components/loaders/SimpleLoader.vue';
import EmDashPlaceholder from '@/components/placeholders/EmDashPlaceholder.vue';

export interface Props {
  loading?: boolean;
  data?: AmPmJobs | Record<string, number | string>;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => ({}),
});

function getCustomerJobs(
  customerJobs:
    | AmPmJobs['customer_am']
    /* eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents */
    | AmPmJobs['customer_pm']
    | number
    | string,
): DailyReportCardListItem[] {
  if (typeof customerJobs !== 'object') return [];
  const result = [];
  for (const customer in customerJobs) {
    result.push({
      name: customer,
      value: customerJobs[customer],
    });
  }
  return result;
}

const amJobs = computed(() => {
  return getCustomerJobs(props.data.customer_am);
});

const pmJobs = computed(() => {
  return getCustomerJobs(props.data.customer_pm);
});
</script>

<template>
  <div :class="$style.dailyReportCard" data-testid="dailyReportCard">
    <div :class="$style.header">
      <dl>
        <div :class="$style.dlItem">
          <dt>Total jobs</dt>
          <dd v-if="!props.loading">
            <EmDashPlaceholder :item="props.data.total" />
          </dd>
        </div>
        <div :class="$style.dlItem">
          <dt>AM / PM jobs</dt>
          <dd v-if="!props.loading">
            <EmDashPlaceholder :item="props.data.total_limit" />
          </dd>
        </div>
        <div :class="$style.dlItem">
          <dt>AM / PM jobs %</dt>
          <dd v-if="!props.loading">
            <EmDashPlaceholder
              :item="props.data.percentage"
              :ext="percentFormat"
            />
          </dd>
        </div>
        <div :class="$style.dlItem">
          <dt>AM</dt>
          <dd v-if="!props.loading">
            <EmDashPlaceholder :item="props.data.am_count" />
          </dd>
        </div>
        <div :class="$style.dlItem">
          <dt>PM</dt>
          <dd v-if="!props.loading">
            <EmDashPlaceholder :item="props.data.pm_count" />
          </dd>
        </div>
      </dl>
    </div>
    <div v-if="props.loading" :class="$style.allJobs">
      <div :class="$style.allJobsItem">
        <div :class="[$style.jobs, $style.loaderJobs]">
          <SimpleLoader :size="64" :width="6" />
        </div>
      </div>
    </div>
    <div :class="$style.allJobs">
      <div :class="$style.allJobsItem">
        <div v-if="!props.loading" :class="$style.jobs">
          <div :class="$style.jobsTitle">AM jobs</div>
          <DailyReportCardJobsDescList v-if="amJobs.length" :items="amJobs" />
          <EmDashPlaceholder v-else />
        </div>
        <div :class="$style.text">
          <b>AM jobs</b> — jobs which should be completed before 12&nbsp;PM
        </div>
      </div>
      <div :class="$style.allJobsItem">
        <div v-if="!props.loading" :class="$style.jobs">
          <div :class="$style.jobsTitle">PM jobs</div>
          <DailyReportCardJobsDescList v-if="pmJobs.length" :items="pmJobs" />
          <EmDashPlaceholder v-else />
        </div>
        <div :class="$style.text">
          <b>PM jobs</b> — jobs which should start after 12&nbsp;PM
        </div>
      </div>
    </div>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.dailyReportCard {
  background-color: colors.$color-gray-100;
  border-radius: 4px;
  padding: 40px;
  max-width: 930px;
}
  .header {
    background-color: colors.$color-white;
    border-radius: 2px;
    border-bottom: 2px solid colors.$color-gray-300;
    margin: 0 0 20px 0;
    padding: 22px 25px;

    dl {
      display: flex;
      justify-content: space-between;
      margin: 0;
    }
      .dlItem {
        text-align: center;
      }
        dt {
          font-weight: 500;
          font-size: 18px;
        }
        dd {
          margin: 8px 0 0 0;
        }
  }

  .allJobs {
    display: flex;
    justify-content: space-between;
    margin: 0 -13px;
  }
    .allJobsItem {
      display: flex;
      flex-direction: column;
      width: 100%;
      margin: 0 13px;
    }
      .jobs {
        background-color: colors.$color-white;
        border-radius: 2px;
        height: 100%;
        margin: 0 0 20px 0;
        padding: 25px;
      }
      .loaderJobs {
        min-height: 280px;
      }
      .jobsTitle {
        font-weight: 500;
        margin: 0 0 15px 0;
      }

  .text {
    color: colors.$color-text-secondary;
  }

  .text,
  .jobs {
    b {
      font-weight: 500;
    }
  }
</style>
